/**
 * Maps a kebab-case fl-icon `name` (e.g. `ui-trophy`, `cool-bell`,
 * `ui-flicon-community`) to the camelCase export key used in `fl-icons.ts`
 * (e.g. `flTrophy`, `coolBell`, `flFliconCommunity`).
 *
 * Naming rule:
 *   ui-foo-bar    → flFooBar       (strip `ui-`, prepend `fl`)
 *   cool-foo      → coolFoo        (camelCase as-is, no `fl` prefix —
 *                                   matches @ng-icons/coolicons export names;
 *                                   flags externally-sourced set distinct from
 *                                   native fl icons)
 *   anything-else → flAnythingElse (prepend `fl`, camelCase whole name,
 *                                   including `flicon-foo` → `flFliconFoo`)
 *
 * Shared between the codegen (`scripts/generate-fl-icons.ts`) and the runtime
 * registry lookup in `icon.component.ts` — keep callers in sync by importing
 * from here.
 *
 * NOTE: a vendored copy lives at `tools/eslint/src/icon-name-to-const.ts` (the
 * ESLint sandbox can't import from `src/`). If you change this function, update
 * that copy too.
 */
export function iconNameToConstName(name: string): string {
  let stem: string;
  let prefix: string;
  if (name.startsWith('ui-')) {
    stem = name.slice(3);
    prefix = 'fl';
  } else if (name.startsWith('cool-')) {
    stem = name;
    prefix = '';
  } else {
    stem = name;
    prefix = 'fl';
  }
  const camel = stem
    .split('-')
    .filter(Boolean)
    .map((part, i) =>
      prefix === '' && i === 0
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join('');
  return `${prefix}${camel}`;
}

/**
 * Inverse of `iconNameToConstName`: const name → kebab `name` (`flTrophy` →
 * `ui-trophy`). Used for the HTTP fallback when a caller writes the fl-ng-icon
 * name. Lossless because every asset is named to round-trip — the codegen
 * (`scripts/generate-fl-icons.ts`) throws on any filename that doesn't, so
 * there is no exception table to consult.
 */
export function constNameToIconName(constName: string): string {
  return reverseByRule(constName);
}

// `cool*` consts keep their kebab name (no `fl` prefix was added); everything
// else had `fl` prepended to a `ui-` icon.
export function reverseByRule(constName: string): string {
  const kebab = deCamel(
    constName.startsWith('fl') ? constName.slice(2) : constName,
  );
  return constName.startsWith('fl') ? `ui-${kebab}` : kebab;
}

// `AddCircle` → `add-circle`, `coolBell` → `cool-bell`. Insert a hyphen before
// each capital, then lowercase. Digits attach to the preceding word (`v2`,
// `house01`), so asset filenames must avoid a `word-digit` hyphen.
function deCamel(camel: string): string {
  return camel
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '');
}
